export interface RemoteFirmwareMetadata {
  fileId: string;
  name: string;
  size: number;
  lastModified: number;
  etag: string;
  kind?: string;
  /** Relative folder below the server catalog root, when provided by the catalog. */
  folder?: string;
}

const REMOTE_FIRMWARE = Symbol("remote-firmware");
const REMOTE_RANGE = Symbol("remote-firmware-range");
/** Read-ahead window used to amortize WAN round-trip latency. */
export const REMOTE_READ_AHEAD_BYTES = 8 * 1024 * 1024;
const REMOTE_RANGE_CACHE_LIMIT = 32 * 1024 * 1024;
const REMOTE_VERSION_VALIDATION_MS = 5_000;

interface CachedRemoteRange {
  key: string;
  start: number;
  end: number;
  bytes: ArrayBuffer;
}

interface PendingRemoteRange {
  controller: AbortController;
  promise: Promise<ArrayBuffer>;
  consumers: number;
  settled: boolean;
}

function normalizeBlobSlice(size: number, start?: number, end?: number): { start: number; end: number } {
  const normalizedStart = start === undefined ? 0 : start < 0 ? Math.max(size + Math.trunc(start), 0) : Math.min(Math.trunc(start), size);
  const normalizedEnd = end === undefined ? size : end < 0 ? Math.max(size + Math.trunc(end), 0) : Math.min(Math.trunc(end), size);
  return { start: Math.min(normalizedStart, size), end: Math.max(Math.min(normalizedEnd, size), normalizedStart) };
}

function waitForAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new DOMException("Operation cancelled", "AbortError"));
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(new DOMException("Operation cancelled", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    );
  });
}

/** A File-compatible lazy source backed by the authenticated firmware Range API. */
export class RemoteFirmwareFile extends File {
  private readonly metadata: RemoteFirmwareMetadata;
  private readonly remoteSize: number;
  private readonly rangeCache = new Map<string, CachedRemoteRange>();
  private readonly pendingRanges = new Map<string, PendingRemoteRange>();
  private rangeCacheBytes = 0;
  private rangeCacheValidatedAt = 0;
  private versionValidation?: Promise<void>;

  constructor(metadata: RemoteFirmwareMetadata) {
    super([], metadata.name, { type: "application/octet-stream", lastModified: metadata.lastModified });
    this.metadata = { ...metadata };
    this.remoteSize = metadata.size;
    Object.defineProperty(this, REMOTE_FIRMWARE, { value: true });
  }

  get size(): number {
    return this.remoteSize;
  }

  get remoteMetadata(): RemoteFirmwareMetadata {
    return { ...this.metadata };
  }

  slice(start?: number, end?: number, contentType?: string): Blob {
    const bounds = normalizeBlobSlice(this.remoteSize, start, end);
    return new RemoteFirmwareRange(this, bounds.start, bounds.end, contentType);
  }

  async readRange(start: number, endExclusive: number, signal?: AbortSignal): Promise<ArrayBuffer> {
    if (start < 0 || endExclusive < start || endExclusive > this.remoteSize) throw new RangeError("Invalid remote firmware range");
    if (start === endExclusive) return new ArrayBuffer(0);
    if (signal?.aborted) throw new DOMException("Operation cancelled", "AbortError");

    if (endExclusive - start > REMOTE_READ_AHEAD_BYTES) {
      const output = new Uint8Array(endExclusive - start);
      let offset = start;
      let outputOffset = 0;
      while (offset < endExclusive) {
        const next = Math.min(offset + REMOTE_READ_AHEAD_BYTES, endExclusive);
        output.set(new Uint8Array(await this.readRange(offset, next, signal)), outputOffset);
        outputOffset += next - offset;
        offset = next;
      }
      return output.buffer;
    }

    const windowStart = Math.floor(start / REMOTE_READ_AHEAD_BYTES) * REMOTE_READ_AHEAD_BYTES;
    const windowEnd = Math.min(this.remoteSize, windowStart + REMOTE_READ_AHEAD_BYTES);
    if (endExclusive > windowEnd) {
      const first = new Uint8Array(await this.readRange(start, windowEnd, signal));
      const second = new Uint8Array(await this.readRange(windowEnd, endExclusive, signal));
      const output = new Uint8Array(first.byteLength + second.byteLength);
      output.set(first);
      output.set(second, first.byteLength);
      return output.buffer;
    }

    const cached = this.findCachedRange(start, endExclusive);
    if (cached) {
      await this.validateCachedVersion(signal);
      return cached.bytes.slice(start - cached.start, endExclusive - cached.start);
    }

    await this.loadRemoteRange(windowStart, windowEnd, signal);
    const loaded = this.findCachedRange(start, endExclusive);
    if (!loaded) throw new Error("Firmware server không cache đủ dữ liệu range.");
    return loaded.bytes.slice(start - loaded.start, endExclusive - loaded.start);
  }

  private findCachedRange(start: number, end: number): CachedRemoteRange | undefined {
    for (const [key, cached] of this.rangeCache) {
      if (cached.start <= start && cached.end >= end) {
        // Map insertion order acts as a small LRU without another dependency.
        this.rangeCache.delete(key);
        this.rangeCache.set(key, cached);
        return cached;
      }
    }
    return undefined;
  }

  private cacheRemoteRange(start: number, end: number, bytes: ArrayBuffer): void {
    if (bytes.byteLength > REMOTE_RANGE_CACHE_LIMIT) return;
    const key = `${start}:${end}`;
    const previous = this.rangeCache.get(key);
    if (previous) this.rangeCacheBytes -= previous.bytes.byteLength;
    this.rangeCache.delete(key);
    while (this.rangeCacheBytes + bytes.byteLength > REMOTE_RANGE_CACHE_LIMIT) {
      const oldest = this.rangeCache.entries().next().value as [string, CachedRemoteRange] | undefined;
      if (!oldest) break;
      this.rangeCache.delete(oldest[0]);
      this.rangeCacheBytes -= oldest[1].bytes.byteLength;
    }
    this.rangeCache.set(key, { key, start, end, bytes });
    this.rangeCacheBytes += bytes.byteLength;
  }

  private async loadRemoteRange(start: number, end: number, signal?: AbortSignal): Promise<ArrayBuffer> {
    const cached = this.findCachedRange(start, end);
    if (cached) {
      await this.validateCachedVersion(signal);
      return cached.bytes;
    }
    const key = `${start}:${end}`;
    let pending = this.pendingRanges.get(key);
    if (!pending) {
      const controller = new AbortController();
      pending = {
        controller,
        promise: Promise.resolve(new ArrayBuffer(0)),
        consumers: 0,
        settled: false,
      };
      pending.promise = this.fetchRemoteRange(start, end, controller.signal)
        .then((bytes) => {
          this.rangeCacheValidatedAt = Date.now();
          this.cacheRemoteRange(start, end, bytes);
          return bytes;
        })
        .finally(() => {
          pending!.settled = true;
          this.pendingRanges.delete(key);
        });
      this.pendingRanges.set(key, pending);
      // If every consumer aborts, the fetch can reject without an unhandled promise.
      void pending.promise.catch(() => undefined);
    }
    pending.consumers += 1;
    try {
      return await waitForAbort(pending.promise, signal);
    } finally {
      pending.consumers -= 1;
      if (pending.consumers === 0 && !pending.settled) pending.controller.abort();
    }
  }

  private async fetchRemoteRange(start: number, endExclusive: number, signal: AbortSignal): Promise<ArrayBuffer> {
    const response = await fetch(`/__firmware/files/${encodeURIComponent(this.metadata.fileId)}/content`, {
      credentials: "same-origin",
      headers: {
        Range: `bytes=${start}-${endExclusive - 1}`,
        "If-Match": this.metadata.etag,
      },
      signal,
    });
    if (response.status === 412) throw new Error("Firmware server file đã thay đổi; hãy chọn lại file.");
    if (!response.ok) throw new Error(`Không đọc được firmware server (HTTP ${response.status}).`);
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength !== endExclusive - start) throw new Error("Firmware server trả về thiếu dữ liệu.");
    return bytes;
  }

  private async validateCachedVersion(signal?: AbortSignal): Promise<void> {
    if (Date.now() - this.rangeCacheValidatedAt <= REMOTE_VERSION_VALIDATION_MS) return;
    if (!this.versionValidation) {
      const validation = fetch(`/__firmware/files/${encodeURIComponent(this.metadata.fileId)}/content`, {
        method: "HEAD",
        credentials: "same-origin",
        headers: { "If-Match": this.metadata.etag },
      }).then((response) => {
        if (response.status === 412) throw new Error("Firmware server file đã thay đổi; hãy chọn lại file.");
        if (!response.ok) throw new Error(`Không xác thực được firmware server (HTTP ${response.status}).`);
        this.rangeCacheValidatedAt = Date.now();
      });
      this.versionValidation = validation.finally(() => { this.versionValidation = undefined; });
    }
    await waitForAbort(this.versionValidation, signal);
  }
}

class RemoteFirmwareRange extends Blob {
  private readonly source: RemoteFirmwareFile;
  private readonly absoluteStart: number;
  private readonly remoteSize: number;
  private readonly contentType: string;

  constructor(source: RemoteFirmwareFile, start: number, end: number, contentType?: string) {
    super([], { type: contentType ?? "application/octet-stream" });
    this.source = source;
    this.absoluteStart = start;
    this.remoteSize = Math.max(0, end - start);
    this.contentType = contentType ?? "application/octet-stream";
    Object.defineProperty(this, REMOTE_RANGE, { value: true });
  }

  get size(): number {
    return this.remoteSize;
  }

  get type(): string {
    return this.contentType;
  }

  slice(start?: number, end?: number, contentType?: string): Blob {
    const bounds = normalizeBlobSlice(this.remoteSize, start, end);
    return new RemoteFirmwareRange(this.source, this.absoluteStart + bounds.start, this.absoluteStart + bounds.end, contentType ?? this.contentType);
  }

  arrayBuffer(): Promise<ArrayBuffer> {
    return this.readArrayBuffer();
  }

  readArrayBuffer(signal?: AbortSignal): Promise<ArrayBuffer> {
    return this.source.readRange(this.absoluteStart, this.absoluteStart + this.remoteSize, signal);
  }

  text(): Promise<string> {
    return this.arrayBuffer().then((buffer) => new TextDecoder().decode(buffer));
  }
}

export function isRemoteFirmwareFile(value: unknown): value is RemoteFirmwareFile {
  return value instanceof RemoteFirmwareFile && Object.prototype.hasOwnProperty.call(value, REMOTE_FIRMWARE);
}

export function isRemoteFirmwareRange(value: unknown): value is RemoteFirmwareRange {
  return value instanceof Blob && Object.prototype.hasOwnProperty.call(value, REMOTE_RANGE);
}

export function firmwareSourceSummary(file: File): string {
  if (!isRemoteFirmwareFile(file)) return "Máy này";
  const etag = file.remoteMetadata.etag.replace(/^"|"$/gu, "").slice(0, 12);
  return `Server · ETag ${etag}`;
}
